import { cn } from 'lib/utils'
import { Sparkles, Upload } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'

const HERO_MODE_OPTIONS = [
  {
    key: 'create',
    label: 'Create with Taka',
    Icon: Sparkles,
    title: 'Taka generates the post from your idea and ingredients.',
  },
  {
    key: 'own',
    label: 'Post your own',
    Icon: Upload,
    title: 'Taka publishes your media exactly as it is.',
  },
] as const
export type HeroMode = (typeof HERO_MODE_OPTIONS)[number]['key']

/**
 * The card-level mode switch: the two rooms of the creation moment, declared
 * UPFRONT (Matan, round 10 — the old on-media toggle asked the intent
 * question after the drop, which read as difficult and unexpected). A true
 * two-segment control, visual sibling of the ShapeSlider (same sliding acid
 * thumb and motion). Switching rooms only flips card state; media and typed
 * words carry across. Shared by the Home hero and the calendar's day-tap
 * popover (the popover is the hero's mini form). At mobile widths the icons
 * drop and padding tightens so BOTH labels stay readable on one line (390px
 * included).
 */
export function HeroModeToggle({
  value,
  disabled,
  isMobile,
  onChange,
}: {
  value: HeroMode
  disabled: boolean
  isMobile: boolean
  onChange: (next: HeroMode) => void
}) {
  const activeIdx = value === 'own' ? 1 : 0
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null)
  // Same measure discipline as the ShapeSlider: segments have different
  // widths, so the thumb tracks the active button's real geometry and
  // re-measures on resize and late webfont swaps.
  useLayoutEffect(() => {
    const measure = () => {
      const el = buttonRefs.current[activeIdx]
      if (el) {
        const { offsetLeft: left, offsetWidth: width } = el
        setThumb(prev =>
          prev && prev.left === left && prev.width === width ? prev : { left, width }
        )
      }
    }
    measure()
    window.addEventListener('resize', measure)
    void document.fonts?.ready.then(measure).catch(() => undefined)
    return () => window.removeEventListener('resize', measure)
  }, [activeIdx])

  return (
    <div
      role="group"
      aria-label="Use materials as"
      className="relative flex flex-shrink-0 items-center rounded-full border border-line bg-bg/60 p-0.5"
    >
      {thumb && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0.5 rounded-full border border-acid/60 bg-acid/15 transition-all duration-200 ease-out"
          style={{ left: thumb.left, width: thumb.width }}
        />
      )}
      {HERO_MODE_OPTIONS.map((opt, i) => (
        <button
          key={opt.key}
          ref={el => {
            buttonRefs.current[i] = el
          }}
          type="button"
          disabled={disabled}
          aria-pressed={i === activeIdx}
          title={opt.title}
          onClick={() => onChange(opt.key)}
          className={cn(
            'relative z-[1] flex items-center gap-1 rounded-full py-0.5 text-[11px] font-medium whitespace-nowrap transition-colors disabled:opacity-60',
            isMobile ? 'px-1.5' : 'px-2',
            i === activeIdx ? 'text-paper' : 'text-paper-faint hover:text-paper'
          )}
        >
          {!isMobile && <opt.Icon size={11} className="flex-shrink-0" />}
          {opt.label}
        </button>
      ))}
    </div>
  )
}
